import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router";

import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { brand } from "../theme/brand";
import { cx } from "./ui";

function NavItem({
  to,
  children,
  onClick,
  end,
}: {
  to: string;
  children: string;
  onClick?: () => void;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        cx(
          "block rounded-md px-3 py-2 text-sm font-semibold",
          isActive ? "bg-white/15 text-white" : "text-brand-50 hover:bg-white/10 hover:text-white",
        )
      }
    >
      {children}
    </NavLink>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const { data: config } = useConfig();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Staff and administrators get the Bill's workspace; customers get the customer portal.
  const links: { to: string; label: string; end?: boolean }[] = [];
  if (user?.is_staff) {
    links.push({ to: "/staff", label: "Production queue", end: true });
    links.push({ to: "/staff/files", label: "Files FROM Bill's" });
    links.push({ to: "/staff/customers", label: "Customers" });
    if (user.is_admin) links.push({ to: "/admin", label: "Admin" });
    links.push({ to: "/account/security", label: "My account" });
  } else if (user) {
    links.push({ to: "/dashboard", label: "Dashboard" });
    links.push({ to: "/orders", label: "Orders TO Bill's" });
    links.push({ to: "/files", label: "Files FROM Bill's" });
    links.push({ to: "/account", label: "My account" });
  }
  const home = user ? (user.is_staff ? "/staff" : "/dashboard") : "/";

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only-focusable absolute left-2 top-2 z-50 rounded bg-white px-3 py-2 font-semibold"
      >
        Skip to main content
      </a>
      <header className="bg-brand-600 text-white print:hidden">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <Link to={home} className="flex items-center gap-3 text-lg font-bold text-white">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt={brand.logoAlt} className="h-9 w-auto" />
            ) : null}
            <span>
              {brand.name}
              <span className="block text-xs font-normal text-brand-100">
                {user?.is_staff ? "Staff workspace" : "Customer Portal"}
              </span>
            </span>
          </Link>
          {user ? (
            <button
              type="button"
              className="rounded-md px-3 py-2 text-sm font-semibold hover:bg-white/10 md:hidden"
              aria-expanded={open}
              aria-controls="main-nav"
              onClick={() => setOpen((o) => !o)}
            >
              Menu
            </button>
          ) : (
            <nav aria-label="Account" className="flex gap-2">
              <NavItem to="/login">Sign in</NavItem>
              <NavItem to="/register">Create account</NavItem>
            </nav>
          )}
        </div>
        {user && (
          <nav
            id="main-nav"
            aria-label="Main"
            className={cx(
              "mx-auto max-w-6xl px-4 pb-3 md:flex md:items-center md:gap-1",
              open ? "block" : "hidden md:flex",
            )}
          >
            {links.map((l) => (
              <NavItem key={l.to} to={l.to} end={l.end} onClick={close}>
                {l.label}
              </NavItem>
            ))}
            <button
              type="button"
              className="mt-1 block rounded-md px-3 py-2 text-left text-sm font-semibold text-brand-50 hover:bg-white/10 md:ml-auto md:mt-0"
              onClick={async () => {
                close();
                await logout();
                navigate("/login", { replace: true });
              }}
            >
              Sign out
            </button>
          </nav>
        )}
      </header>

      <main id="main" tabIndex={-1} className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:py-8">
        <Outlet />
      </main>

      <footer className="border-t border-line bg-surface print:hidden">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-5 text-sm text-ink-muted sm:flex-row sm:justify-between">
          <p>
            {brand.name}
            {config?.contact && (
              <>
                {" "}
                · {config.contact.address} ·{" "}
                <a className="underline" href={`tel:${config.contact.phone}`}>
                  {config.contact.phone}
                </a>{" "}
                ·{" "}
                <a className="underline" href={`mailto:${config.contact.email}`}>
                  {config.contact.email}
                </a>
              </>
            )}
          </p>
          <p className="flex gap-4">
            <Link className="underline" to="/privacy">
              Privacy
            </Link>
            <Link className="underline" to="/terms">
              Terms of use
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
